import { useCallback } from 'react';

import { openWindow } from '@change-corgi/core/window';
import type { I18nUtilities } from '@change-corgi/core-react/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';
import type { TrackingFn } from '@change-corgi/core-react/tracking';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useMovementPageData } from '#src/app/pages/movement/pageContext';
import { useCurrentUser } from '#src/app/shared/hooks/session';
import type { ShortUrlInfo } from '#src/app/shared/hooks/shortUrls';
import { shareFacebook } from '#src/app/shared/share/facebook';
import { shareTwitter } from '#src/app/shared/share/twitter';
import { shareWhatsapp } from '#src/app/shared/share/whatsapp';

import { useShareUrl } from './useSocialShares';

type VoidFunction = () => void;
type Social = 'facebook' | 'twitter' | 'email' | 'whatsapp';
type ShareUrls = Record<Social, ShortUrlInfo>;

export const MOVEMENT_EVENT_VIEW_CONTEXT = 'movement_show';

const FB_TRACKING_EVENTS = {
	success: 'movement_share_success',
	intent: 'movement_share_intent',
	legacy_success: 'movement_share',
	cancel: 'movement_share_cancel',
	failure: 'movement_share_failure',
};

const TWITTER_TRACKING_EVENTS = { success: 'movement_share' };
const WHATSAPP_TRACKING_EVENTS = { success: 'movement_share' };

const TRACKING_DATA = {
	context: MOVEMENT_EVENT_VIEW_CONTEXT,
	viewContext: MOVEMENT_EVENT_VIEW_CONTEXT,
};

type ShareCtx = {
	shareUrls: ShareUrls;
	movementId: string;
	objective: string;
	translate: I18nUtilities['translate'];
	userName: string;
	countryCode: string | undefined;
	utilityContext: UtilityContext;
	track: TrackingFn;
};

function buildTrackingData(channel: Social, info: ShortUrlInfo, movementId: string) {
	return {
		...TRACKING_DATA,
		location: channel,
		movement_id: movementId,
		...info.trackingProperties,
	};
}

function useFacebookClick({ shareUrls, movementId, utilityContext, track }: ShareCtx): VoidFunction {
	return useCallback(() => {
		void shareFacebook(
			shareUrls.facebook.url,
			{
				trackingData: buildTrackingData('facebook', shareUrls.facebook, movementId),
				trackingEvents: FB_TRACKING_EVENTS,
			},
			utilityContext,
			track,
		);
	}, [movementId, shareUrls, utilityContext, track]);
}

function useTwitterClick({
	shareUrls,
	movementId,
	objective,
	translate,
	countryCode,
	utilityContext,
	track,
}: ShareCtx): VoidFunction {
	return useCallback(
		() =>
			shareTwitter({
				url: shareUrls.twitter.url,
				comment: translate('corgi.movement.show.share.twitter', { objective }),
				options: {
					trackingData: buildTrackingData('twitter', shareUrls.twitter, movementId),
					trackingEvents: TWITTER_TRACKING_EVENTS,
					countryCode,
				},
				track,
				utilityContext,
			}),
		[countryCode, movementId, shareUrls, objective, translate, track, utilityContext],
	);
}

function useEmailClick({ shareUrls, movementId, objective, translate, userName, track }: ShareCtx): VoidFunction {
	return useCallback(() => {
		const encodedSubject = encodeURIComponent(objective);
		const encodedBody = encodeURIComponent(
			translate('corgi.movement.show.share.email_body', {
				objective,
				url: shareUrls.email.url,
				from: userName,
			}),
		);
		const emailHref = `mailto:?body=${encodedBody}&subject=${encodedSubject}`;
		void track('movement_share', {
			movement_id: movementId,
			location: 'email',
			context: MOVEMENT_EVENT_VIEW_CONTEXT,
			view_context: MOVEMENT_EVENT_VIEW_CONTEXT,
			...shareUrls.email.trackingProperties,
		});
		openWindow(emailHref);
	}, [objective, translate, shareUrls.email, userName, movementId, track]);
}

function useWhatsAppClick({ shareUrls, movementId, objective, track }: ShareCtx): VoidFunction {
	return useCallback(() => {
		shareWhatsapp(
			objective,
			shareUrls.whatsapp.url,
			{
				trackingEvents: WHATSAPP_TRACKING_EVENTS,
				trackingData: buildTrackingData('whatsapp', shareUrls.whatsapp, movementId),
			},
			track,
		);
	}, [objective, shareUrls.whatsapp, movementId, track]);
}

// TODO migrate to using useAvailableShareAction()
export function useShareClickHandlers(): {
	onEmailClick: VoidFunction;
	onFacebookClick: VoidFunction;
	onTwitterClick: VoidFunction;
	onWhatsAppClick: VoidFunction;
} {
	const {
		data: {
			movement: { id: movementId, objective },
		},
	} = useMovementPageData();

	const currentUser = useCurrentUser();
	const ctx: ShareCtx = {
		shareUrls: useShareUrl({ slug: movementId }),
		movementId,
		objective,
		translate: useI18n().translate,
		userName: currentUser?.shortDisplayName ?? '',
		countryCode: currentUser?.country?.countryCode,
		utilityContext: useUtilityContext(),
		track: useTracking(),
	};

	return {
		onFacebookClick: useFacebookClick(ctx),
		onTwitterClick: useTwitterClick(ctx),
		onEmailClick: useEmailClick(ctx),
		onWhatsAppClick: useWhatsAppClick(ctx),
	};
}
